/**
 * Robust Anti-Piracy Watermark System
 * 
 * Features:
 * - Multiple faint semi-transparent watermarks
 * - Smooth random movement using Perlin noise
 * - Blur effect makes edge detection difficult
 * - Dynamic opacity variation
 * - User identification for forensic tracking
 * 
 * Performance Optimizations:
 * - Offscreen canvas caching (pre-render blurred watermark)
 * - Reduced blur passes (6 → 2)
 * - Update throttling (every 3 frames)
 * - Cached font measurements
 * - Lower blur values for better GPU performance
 */

// Perlin Noise implementation for smooth random movement
class PerlinNoise {
  private permutation: number[] = [];
  
  constructor(seed: number = Math.random() * 10000) {
    // Initialize permutation table
    const p: number[] = [];
    for (let i = 0; i < 256; i++) {
      p[i] = i;
    }
    
    // Shuffle using seed
    let n = seed;
    for (let i = 255; i > 0; i--) {
      n = (n * 16807) % 2147483647;
      const j = n % (i + 1);
      [p[i], p[j]] = [p[j], p[i]];
    }
    
    // Duplicate for overflow
    this.permutation = [...p, ...p];
  }
  
  private fade(t: number): number {
    return t * t * t * (t * (t * 6 - 15) + 10);
  }
  
  private lerp(a: number, b: number, t: number): number {
    return a + t * (b - a);
  }
  
  private grad(hash: number, x: number, y: number): number {
    const h = hash & 3;
    const u = h < 2 ? x : y;
    const v = h < 2 ? y : x;
    return ((h & 1) === 0 ? u : -u) + ((h & 2) === 0 ? v : -v);
  }
  
  public noise2D(x: number, y: number): number {
    const X = Math.floor(x) & 255;
    const Y = Math.floor(y) & 255;
    
    const xf = x - Math.floor(x);
    const yf = y - Math.floor(y);
    
    const u = this.fade(xf);
    const v = this.fade(yf);
    
    const p = this.permutation;
    const aa = p[p[X] + Y];
    const ab = p[p[X] + Y + 1];
    const ba = p[p[X + 1] + Y];
    const bb = p[p[X + 1] + Y + 1];
    
    return this.lerp(
      this.lerp(this.grad(aa, xf, yf), this.grad(ba, xf - 1, yf), u),
      this.lerp(this.grad(ab, xf, yf - 1), this.grad(bb, xf - 1, yf - 1), u),
      v
    );
  }
}

export interface RobustWatermarkConfig {
  // Text to display (usually username/user ID)
  text: string;
  
  // Number of watermark instances
  instanceCount: number;
  
  // Base opacity (0-1), typically very low like 0.08-0.15
  opacity: number;
  
  // Blur amount in pixels (reduced for performance)
  blurAmount: number;
  
  // Font size as percentage of canvas height
  fontSizePercent: number;
  
  // Movement speed (lower = slower, more subtle)
  movementSpeed: number;
  
  // Opacity variation range (watermarks will pulse between opacity +/- this value)
  opacityVariation: number;
  
  // Color of watermark (with alpha will be applied separately)
  color: string;
  
  // Enable rotation variation
  enableRotation: boolean;
  
  // Maximum rotation angle in degrees
  maxRotation: number;
  
  // Position range - how much of canvas to use (0-1, where 1 = full canvas)
  positionRange: number;
}

export interface WatermarkInstance {
  id: number;
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  noiseOffsetX: number;
  noiseOffsetY: number;
  noiseOffsetOpacity: number;
  baseOpacity: number;
  rotation: number;
  scale: number;
  // Cached dynamic opacity to avoid recalculating every frame
  currentOpacity: number;
}

export class RobustWatermarkRenderer {
  private config: RobustWatermarkConfig;
  private instances: WatermarkInstance[] = [];
  private perlinX: PerlinNoise;
  private perlinOpacity: PerlinNoise;
  private time: number = 0;
  private canvas: HTMLCanvasElement | null = null;
  private isEnabled: boolean = true;
  
  // Performance optimization: Offscreen canvas cache
  private cachedWatermark: HTMLCanvasElement | null = null;
  private cacheNeedsUpdate: boolean = true;
  private lastCacheWidth: number = 0;
  private lastCacheHeight: number = 0;
  private lastCacheText: string = '';
  
  // Performance optimization: Font measurement cache
  private cachedTextWidth: number = 0;
  private cachedFontSize: number = 200;
  
  // Performance optimization: Update throttling
  private updateCounter: number = 0;
  private readonly updateFrequency: number = 3; // Update every 3 frames
  
  // Performance optimization: Opacity update throttling (slower than position)
  private opacityUpdateCounter: number = 0;
  private readonly opacityUpdateFrequency: number = 6; // Update opacity every 6 frames
  
  constructor(config: Partial<RobustWatermarkConfig> = {}) {
    this.config = {
      text: 'USER',
      instanceCount: 1,
      opacity: 0.15, // Increased for visibility with cached rendering
      blurAmount: 6, // Reduced from 12 for better performance
      fontSizePercent: 0.065,
      movementSpeed: 0.5,  // Speed multiplier (0.5 = slow smooth drift)
      opacityVariation: 0.04,
      color: '#FFFFFF',
      enableRotation: true,
      maxRotation: 10,
      positionRange: 1.0,
      ...config,
    };
    
    // Initialize Perlin noise generators with different seeds
    this.perlinX = new PerlinNoise(Math.random() * 10000);
    this.perlinOpacity = new PerlinNoise(Math.random() * 10000 + 10000);
  }
  
  /**
   * Generate a random position across the full canvas from different quadrants
   */
  private getRandomPosition(avoidX?: number, avoidY?: number): { x: number; y: number } {
    const margin = 0.08;
    let x: number, y: number;
    
    // If we have a position to avoid, try to pick a position far from it
    if (avoidX !== undefined && avoidY !== undefined) {
      // Pick a position in a different area of the canvas
      // Divide canvas into 9 zones and try to pick from a different zone
      const zones = [
        { x: 0.15, y: 0.15 },  // top-left
        { x: 0.5, y: 0.15 },   // top-center
        { x: 0.85, y: 0.15 },  // top-right
        { x: 0.15, y: 0.5 },   // middle-left
        { x: 0.5, y: 0.5 },    // center
        { x: 0.85, y: 0.5 },   // middle-right
        { x: 0.15, y: 0.85 },  // bottom-left
        { x: 0.5, y: 0.85 },   // bottom-center
        { x: 0.85, y: 0.85 },  // bottom-right
      ];
      
      // Filter zones that are far from current position
      const farZones = zones.filter(zone => {
        const dist = Math.sqrt(Math.pow(zone.x - avoidX, 2) + Math.pow(zone.y - avoidY, 2));
        return dist > 0.4; // Must be at least 40% of canvas away
      });
      
      // Pick a random far zone and add some randomness
      const zone = farZones.length > 0 
        ? farZones[Math.floor(Math.random() * farZones.length)]
        : zones[Math.floor(Math.random() * zones.length)];
      
      // Add randomness within the zone (±15%)
      x = zone.x + (Math.random() - 0.5) * 0.3;
      y = zone.y + (Math.random() - 0.5) * 0.3;
      
      // Clamp to valid range
      x = Math.max(margin, Math.min(1 - margin, x));
      y = Math.max(margin, Math.min(1 - margin, y));
    } else {
      // No position to avoid, just pick random
      x = margin + Math.random() * (1 - margin * 2);
      y = margin + Math.random() * (1 - margin * 2);
    }
    
    return { x, y };
  }

  /**
   * Initialize watermark instances with random starting positions
   */
  public initialize(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    this.instances = [];
    this.cacheNeedsUpdate = true; // Force cache rebuild
    
    for (let i = 0; i < this.config.instanceCount; i++) {
      const startPos = this.getRandomPosition();
      // Get target position that's far from start position
      const targetPos = this.getRandomPosition(startPos.x, startPos.y);
      
      this.instances.push({
        id: i,
        x: startPos.x,
        y: startPos.y,
        targetX: targetPos.x,
        targetY: targetPos.y,
        noiseOffsetX: Math.random() * 1000,
        noiseOffsetY: Math.random() * 1000,
        noiseOffsetOpacity: Math.random() * 1000,
        baseOpacity: this.config.opacity + (Math.random() - 0.5) * 0.02,
        rotation: (Math.random() - 0.5) * this.config.maxRotation * 2,
        scale: 0.9 + Math.random() * 0.2,
        currentOpacity: this.config.opacity,
      });
    }
  }
  
  /**
   * Update watermark positions - smooth constant velocity movement across full canvas
   * Performance optimized: Only updates every N frames
   */
  public update(): void {
    if (!this.isEnabled) return;
    
    // Throttle updates to every N frames for better performance
    this.updateCounter++;
    const shouldUpdatePosition = this.updateCounter % this.updateFrequency === 0;
    
    // Opacity updates even less frequently
    this.opacityUpdateCounter++;
    const shouldUpdateOpacity = this.opacityUpdateCounter % this.opacityUpdateFrequency === 0;
    
    // Always update time for smooth animation
    this.time += 0.001;
    
    if (!shouldUpdatePosition && !shouldUpdateOpacity) return;
    
    // Constant movement speed per frame (adjusted by config and throttle)
    const speed = this.config.movementSpeed * 0.001 * this.updateFrequency;
    
    for (const instance of this.instances) {
      if (shouldUpdatePosition) {
        // Calculate direction to target
        const dx = instance.targetX - instance.x;
        const dy = instance.targetY - instance.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // If close to target, pick a new random target far from current position
        if (distance < 0.03) {
          const newTarget = this.getRandomPosition(instance.x, instance.y);
          instance.targetX = newTarget.x;
          instance.targetY = newTarget.y;
        } else {
          // Constant velocity movement (not easing) - smoother and actually reaches target
          const normalizedDx = dx / distance;
          const normalizedDy = dy / distance;
          instance.x += normalizedDx * speed;
          instance.y += normalizedDy * speed;
        }
        
        // Update rotation using Perlin noise for smooth variation
        if (this.config.enableRotation) {
          const rotationNoise = this.perlinX.noise2D(
            instance.noiseOffsetX + this.time * 0.5,
            instance.id * 0.2 + 100
          );
          instance.rotation = rotationNoise * this.config.maxRotation;
        }
      }
      
      // Update opacity less frequently (it's subtle anyway)
      if (shouldUpdateOpacity) {
        const opacityNoise = this.perlinOpacity.noise2D(
          instance.noiseOffsetOpacity + this.time * 2,
          instance.id * 0.3
        );
        instance.currentOpacity = Math.max(
          0.08,  // Increased minimum opacity for visibility
          Math.min(
            0.25, // Increased maximum opacity for visibility
            instance.baseOpacity + opacityNoise * this.config.opacityVariation
          )
        );
      }
    }
  }
  
  /**
   * Create or update the cached watermark texture
   * This pre-renders the blurred text to an offscreen canvas
   */
  private updateCache(canvasWidth: number, canvasHeight: number): void {
    // Check if cache needs update
    const needsUpdate = this.cacheNeedsUpdate ||
      this.lastCacheWidth !== canvasWidth ||
      this.lastCacheHeight !== canvasHeight ||
      this.lastCacheText !== this.config.text;
    
    if (!needsUpdate && this.cachedWatermark) return;
    
    // Create offscreen canvas at reduced resolution for performance
    // 0.5 scale = 4x fewer pixels to process
    const cacheScale = 0.5;
    const cacheWidth = Math.ceil(canvasWidth * cacheScale);
    const cacheHeight = Math.ceil(canvasHeight * cacheScale);
    
    if (!this.cachedWatermark) {
      this.cachedWatermark = document.createElement('canvas');
    }
    
    this.cachedWatermark.width = cacheWidth;
    this.cachedWatermark.height = cacheHeight;
    
    const ctx = this.cachedWatermark.getContext('2d');
    if (!ctx) return;
    
    // Clear the cache canvas
    ctx.clearRect(0, 0, cacheWidth, cacheHeight);
    
    const blur = this.config.blurAmount;
    const fontSize = this.cachedFontSize;
    
    // Measure text width (cache this)
    ctx.font = `${fontSize}px Arial, sans-serif`;
    this.cachedTextWidth = ctx.measureText(this.config.text).width;
    
    // Calculate scale to fit 98% of canvas width
    const maxTextWidth = cacheWidth * 0.98;
    const scaleX = maxTextWidth / this.cachedTextWidth;
    
    // Scale Y to stretch height to cover 90% of canvas height
    const targetHeight = cacheHeight * 0.90;
    const scaleY = targetHeight / fontSize;
    
    // Position at center
    ctx.save();
    ctx.translate(cacheWidth / 2, cacheHeight / 2);
    ctx.scale(scaleX, scaleY);
    
    // Set text properties
    ctx.font = `${fontSize}px Arial, sans-serif`;
    ctx.fillStyle = this.config.color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // OPTIMIZED: Only 2 blur passes instead of 6
    // This reduces GPU load by ~70% while maintaining visual effect
    // NOTE: Draw at full opacity here - dynamic opacity is applied during render()
    
    // Pass 1: Soft outer glow (moderate blur)
    ctx.shadowColor = this.config.color;
    ctx.shadowBlur = blur * 2; // 12px instead of 72px
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.globalAlpha = 1.0; // Full opacity - dynamic opacity applied in render()
    ctx.fillText(this.config.text, 0, 0);
    
    // Pass 2: Core text with light blur
    ctx.shadowBlur = blur; // 6px instead of 60px
    ctx.globalAlpha = 1.0;
    ctx.fillText(this.config.text, 0, 0);
    
    ctx.restore();
    
    // Update cache metadata
    this.lastCacheWidth = canvasWidth;
    this.lastCacheHeight = canvasHeight;
    this.lastCacheText = this.config.text;
    this.cacheNeedsUpdate = false;
  }
  
  /**
   * Render all watermark instances to the canvas context
   * Performance optimized: Uses cached watermark texture
   */
  public render(ctx: CanvasRenderingContext2D, canvasWidth: number, canvasHeight: number): void {
    if (!this.isEnabled || !this.config.text) return;
    
    // Update cache if needed (this is fast if cache is valid)
    this.updateCache(canvasWidth, canvasHeight);
    
    if (!this.cachedWatermark) return;
    
    const cacheWidth = this.cachedWatermark.width;
    const cacheHeight = this.cachedWatermark.height;
    
    for (const instance of this.instances) {
      ctx.save();
      
      // Calculate actual pixel positions
      const x = instance.x * canvasWidth;
      const y = instance.y * canvasHeight;
      
      // Use pre-calculated opacity from update()
      const dynamicOpacity = instance.currentOpacity;
      
      // Apply transformations
      ctx.translate(x, y);
      ctx.rotate((instance.rotation * Math.PI) / 180);
      
      // Apply instance-specific scale
      ctx.scale(instance.scale, instance.scale);
      
      // Set opacity
      ctx.globalAlpha = dynamicOpacity;
      
      // Draw the cached watermark centered at the position
      // Scale from cache resolution to full resolution
      const drawWidth = canvasWidth;
      const drawHeight = canvasHeight;
      ctx.drawImage(
        this.cachedWatermark,
        -drawWidth / 2,
        -drawHeight / 2,
        drawWidth,
        drawHeight
      );
      
      ctx.restore();
    }
  }
  
  /**
   * Update configuration
   */
  public updateConfig(config: Partial<RobustWatermarkConfig>): void {
    const oldText = this.config.text;
    this.config = { ...this.config, ...config };
    
    // Mark cache for rebuild if text changed
    if (config.text !== undefined && config.text !== oldText) {
      this.cacheNeedsUpdate = true;
    }
    
    // Update instance count if changed
    if (config.instanceCount !== undefined && this.canvas) {
      this.initialize(this.canvas);
    }
  }
  
  /**
   * Enable/disable the watermark
   */
  public setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }
  
  /**
   * Check if watermark is enabled
   */
  public getEnabled(): boolean {
    return this.isEnabled;
  }
  
  /**
   * Get current configuration
   */
  public getConfig(): RobustWatermarkConfig {
    return { ...this.config };
  }
  
  /**
   * Get instance positions (for debugging)
   */
  public getInstances(): WatermarkInstance[] {
    return [...this.instances];
  }
  
  /**
   * Reset time and reinitialize positions
   */
  public reset(): void {
    this.time = 0;
    this.updateCounter = 0;
    this.opacityUpdateCounter = 0;
    this.cacheNeedsUpdate = true;
    if (this.canvas) {
      this.initialize(this.canvas);
    }
  }
  
  /**
   * Force cache rebuild (call after canvas resize)
   */
  public invalidateCache(): void {
    this.cacheNeedsUpdate = true;
  }
  
  /**
   * Clean up resources
   */
  public dispose(): void {
    this.cachedWatermark = null;
    this.canvas = null;
    this.instances = [];
  }
}

/**
 * Factory function to create a robust watermark renderer with preset configurations
 * Performance optimized presets with lower blur values
 */
export function createRobustWatermark(
  username: string,
  preset: 'subtle' | 'moderate' | 'strong' = 'moderate'
): RobustWatermarkRenderer {
  const presets: Record<string, Partial<RobustWatermarkConfig>> = {
    // Faint and blurry - covers most of screen
    subtle: {
      text: username,
      instanceCount: 1,
      opacity: 0.12,  // Increased from 0.06 to compensate for cached rendering
      blurAmount: 6,  // Reduced from 15 for better GPU performance
      fontSizePercent: 0.85,  // 85% height coverage
      movementSpeed: 0.3, 
      opacityVariation: 0.03,
      color: '#FFFFFF',
      enableRotation: true,
      maxRotation: 5,
      positionRange: 1.0,
    },
    // Blurry, covers most of screen - balanced
    moderate: {
      text: username,
      instanceCount: 1,
      opacity: 0.15,  // Increased from 0.08 to compensate for cached rendering
      blurAmount: 6,  // Reduced from 12 for better GPU performance
      fontSizePercent: 0.85,  // 85% height coverage
      movementSpeed: 0.5, 
      opacityVariation: 0.04,
      color: '#FFFFFF',
      enableRotation: true,
      maxRotation: 5,
      positionRange: 1.0,
    },
    // Maximum coverage - AI removal would ruin video
    strong: {
      text: username,
      instanceCount: 1,
      opacity: 0.18,  // Increased from 0.10 to compensate for cached rendering
      blurAmount: 5,  // Reduced from 10 for better GPU performance
      fontSizePercent: 0.85,  // 85% height coverage
      movementSpeed: 0.7, 
      opacityVariation: 0.05,
      color: '#FFFFFF',
      enableRotation: true,
      maxRotation: 5,
      positionRange: 1.0,
    },
  };
  
  return new RobustWatermarkRenderer(presets[preset]);
}
